import {
  Modal,
  Form,
  ModalProps,
  FormProps,
  Row,
  Col,
  Select,
  useSelect,
  Button,
  message,
  useForm,
} from '@pankod/refine-antd'
import { useCreate, useGetIdentity, useList, useMany, useOne } from '@pankod/refine-core'
import { IProduct, ITestTrack, IPaperTemplate, IPaperRequest } from 'interfaces'
import { useUserContext } from 'context/user'
import { useState } from 'react'
import { generateTrackBasedPaperRequestName } from 'helpers/paper/paperRequestName'

interface ICreatePaperNoEventModal {
  modalProps: ModalProps
  formProps: FormProps
  close?: any
  onPaperRequestCreated?: (paperRequest: IPaperRequest) => void
}

export const CreatePaperNoEventModal: React.FC<ICreatePaperNoEventModal> = ({
  modalProps,
  formProps: initialFormProps,
  close,
  onPaperRequestCreated,
}) => {
  const { data: user } = useGetIdentity()
  const { paperApproverList, paperSetterList } = useUserContext()
  const { mutateAsync: createPaperRequest } = useCreate<IPaperRequest>()
  const { formProps } = useForm()

  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [selectedTestTrackId, setSelectedTestTrackId] = useState<string>('')

  // Product select
  const { selectProps: productSelectProps } = useSelect<IProduct>({
    resource: 'products',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    metaData: {
      fields: ['id', 'name', 'test_track_ids', 'type'],
    },
    sort: [{ field: 'name', order: 'asc' }],
    queryOptions: { enabled: !!modalProps?.visible },
  })

  // The select's options only carry {label, value}, so read test_track_ids off the product record itself
  const { data: selectedProductData } = useOne<IProduct>({
    resource: 'products',
    id: selectedProductId,
    queryOptions: {
      enabled: !!selectedProductId,
    },
    metaData: {
      fields: ['id', 'name', 'test_track_ids', 'type'],
    },
  })

  const productTestTrackIds: string[] = selectedProductData?.data?.test_track_ids ?? []

  const { data: testTracksData, isFetching: testTracksLoading } = useMany<ITestTrack>({
    resource: 'test_tracks',
    ids: productTestTrackIds,
    queryOptions: {
      enabled: productTestTrackIds.length > 0,
    },
    metaData: {
      fields: ['id', 'name', 'subject_ids', 'in_use'],
    },
  })

  const inUseTracks = testTracksData?.data?.filter((tt: ITestTrack) => tt.in_use === true) ?? []

  // Paper templates for product and test track
  const { data: paperTemplatesData, isFetching: paperTemplatesLoading } = useList<IPaperTemplate>({
    resource: 'paper_templates',
    config: {
      filters: [
        {
          field: 'product_id',
          operator: 'eq',
          value: selectedProductId,
        },
        {
          field: 'test_track_id',
          operator: 'eq',
          value: selectedTestTrackId,
        },
        {
          field: 'is_completed',
          operator: 'eq',
          value: true,
        },
      ],
      hasPagination: false,
    },
    queryOptions: {
      enabled: !!selectedProductId && !!selectedTestTrackId,
    },
    metaData: {
      fields: ['id', 'name', 'product_id'],
    },
  })

  const paperTemplates = paperTemplatesData?.data || []

  const onFinish = async (values: any) => {
    try {
      const testTrack = inUseTracks?.find((tt: any) => tt.id === selectedTestTrackId)
      const generatedName = await generateTrackBasedPaperRequestName({
        testTrackId: selectedTestTrackId,
        testTrackName: testTrack?.name,
        productType: selectedProductData?.data?.type,
      })

      const result = await createPaperRequest({
        resource: 'paper_requests',
        values: {
          name: generatedName,
          status: 'Open',
          product_id: selectedProductId,
          test_track_id: selectedTestTrackId,
          paper_template_id: values.paper_template_id,
          paper_setter_id: values.paper_setter_id,
          paper_approver_id: values.paper_approver_id,
          created_by: user?.id,
          test_event_id: null,
          slot_data: null,
          request_type: 'normal',
        },
      })

      message.success('Paper request created successfully!')

      if (onPaperRequestCreated && result?.data) {
        onPaperRequestCreated(result.data)
      }

      formProps?.form?.resetFields()
      setSelectedProductId('')
      setSelectedTestTrackId('')
      close?.()
    } catch (err) {
      message.error('Failed to create paper request. Please try again.')
      console.error(err)
    }
  }

  return (
    <Modal
      {...modalProps}
      title="Create Paper (Direct)"
      width="70%"
      footer={null}
      afterClose={() => {
        formProps?.form?.resetFields()
        setSelectedProductId('')
        setSelectedTestTrackId('')
      }}
    >
      <Form {...formProps} layout="vertical" onFinish={onFinish} autoComplete="off">
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Product"
              name="product_id"
              rules={[{ required: true, message: 'Please select a product' }]}
            >
              <Select
                placeholder="Select a product"
                optionLabelProp="label"
                options={productSelectProps.options}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value: string) => {
                  setSelectedProductId(value)
                  setSelectedTestTrackId('')
                  formProps?.form?.setFieldsValue({ test_track_id: undefined, paper_template_id: undefined })
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Test Track"
              name="test_track_id"
              rules={[{ required: true, message: 'Please select a test track' }]}
            >
              <Select
                placeholder="Select a test track"
                disabled={!selectedProductId}
                loading={testTracksLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value: string) => {
                  setSelectedTestTrackId(value)
                  formProps?.form?.setFieldsValue({ paper_template_id: undefined })
                }}
              >
                {inUseTracks?.map((tt: ITestTrack) => (
                  <Select.Option key={tt.id} value={tt.id}>
                    {tt.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Paper Template"
              name="paper_template_id"
              rules={[{ required: true, message: 'Please select a paper template' }]}
            >
              <Select
                placeholder="Select a paper template"
                disabled={!selectedTestTrackId}
                loading={paperTemplatesLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {paperTemplates?.map((pt: IPaperTemplate) => (
                  <Select.Option key={pt.id} value={pt.id}>
                    {pt.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Paper Setter"
              name="paper_setter_id"
              rules={[{ required: true, message: 'Please select a paper setter' }]}
            >
              <Select
                placeholder="Select a paper setter"
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {paperSetterList?.map((setter: any) => (
                  <Select.Option key={setter.id} value={setter.id}>
                    {setter.displayName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Paper Approver"
              name="paper_approver_id"
              rules={[{ required: true, message: 'Please select a paper approver' }]}
            >
              <Select
                placeholder="Select a paper approver"
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {paperApproverList?.map((approver: any) => (
                  <Select.Option key={approver.id} value={approver.id}>
                    {approver.displayName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]} justify="end" style={{ marginTop: '1rem' }}>
          <Col>
            <Button onClick={close}>Cancel</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit">
              Create Paper Request
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

export default CreatePaperNoEventModal
